import { Avatar, AvatarFallback, getAvatarColor } from '@heidi/design-system/avatar';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@heidi/design-system/dialog';
import { FramedIcon } from '@heidi/design-system/framed-icon';
import { SearchInput } from '@heidi/design-system/search-input';
import { focusRing } from '@heidi/design-system/styles';
import { TypographyCaption, TypographyP2 } from '@heidi/design-system/typography';
import { cn, getInitials } from '@heidi/utils';
import { differenceInCalendarDays } from 'date-fns';
import { AudioLines, Check, UsersRound } from 'lucide-react';
import { useMemo } from 'react';
import { useIntl } from 'react-intl';

import {
  linkedContextItemKey,
  type LinkedContextItem,
} from '../../../contracts/projects/linked-context';
import { contextPickerDialogMessages as messages } from './ContextPickerDialog.messages';

type SessionPickerItem = Extract<LinkedContextItem, { type: 'session' }>;
type SessionPickerGroupKey = 'upcoming' | 'today' | 'yesterday' | 'older';

interface SessionPickerGroup {
  key: SessionPickerGroupKey;
  items: SessionPickerItem[];
}

const SESSION_PICKER_GROUP_ORDER: readonly SessionPickerGroupKey[] = [
  'upcoming',
  'today',
  'yesterday',
  'older',
];

/** Uses the same calendar-day buckets and order as Scribe's session list. */
export const groupSessionPickerItems = (
  items: readonly LinkedContextItem[],
  now: Date
): SessionPickerGroup[] => {
  const groups = new Map<SessionPickerGroupKey, SessionPickerItem[]>(
    SESSION_PICKER_GROUP_ORDER.map((key) => [key, []])
  );

  for (const item of items) {
    if (item.type !== 'session') continue;
    const dayDelta =
      item.createdAt === undefined
        ? Number.NEGATIVE_INFINITY
        : differenceInCalendarDays(new Date(item.createdAt), now);
    const key: SessionPickerGroupKey =
      dayDelta > 0
        ? 'upcoming'
        : dayDelta === 0
          ? 'today'
          : dayDelta === -1
            ? 'yesterday'
            : 'older';
    groups.get(key)?.push(item);
  }

  return SESSION_PICKER_GROUP_ORDER.flatMap((key) => {
    const groupItems = groups.get(key) ?? [];
    return groupItems.length > 0 ? [{ key, items: groupItems }] : [];
  });
};

interface ContextPickerDialogProps {
  type: 'sessions' | 'patients';
  open: boolean;
  items: readonly LinkedContextItem[];
  linkedKeys: ReadonlySet<string>;
  query: string;
  onQueryChange: (query: string) => void;
  onPick: (item: LinkedContextItem) => void;
  onOpenChange: (open: boolean) => void;
}

export const ContextPickerDialog = ({
  type,
  open,
  items,
  linkedKeys,
  query,
  onQueryChange,
  onPick,
  onOpenChange,
}: ContextPickerDialogProps): JSX.Element => {
  const intl = useIntl();
  const isSessions = type === 'sessions';
  const title = intl.formatMessage(
    isSessions ? messages.addSession : messages.addPatient
  );
  const searchLabel = intl.formatMessage(
    isSessions ? messages.searchSessions : messages.searchPatients
  );
  const emptyMessage = intl.formatMessage(
    isSessions ? messages.noSessionsFound : messages.noPatientsFound
  );
  const sessionGroups = useMemo(
    () => groupSessionPickerItems(items, new Date()),
    [items]
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        aria-describedby={undefined}
        className="max-w-2xl gap-0 overflow-hidden p-0 sm:rounded-2xl"
      >
        <DialogHeader className="gap-0 space-y-0 px-6 pb-4 pt-6 text-start">
          <div className="flex items-center gap-3 pe-10">
            <FramedIcon
              state="neutral"
              size="md"
              icon={isSessions ? <AudioLines /> : <UsersRound />}
            />
            <DialogTitle>{title}</DialogTitle>
          </div>
        </DialogHeader>

        <div className="px-6 pb-4">
          <SearchInput
            autoFocus
            value={query}
            onChange={(event) => onQueryChange(event.target.value)}
            placeholder={searchLabel}
            aria-label={searchLabel}
            clearAriaLabel={intl.formatMessage(messages.clearSearch)}
          />
        </div>

        <div className="border-t border-border px-4 py-3">
          <div className="scrollbar-subtle max-h-[min(30rem,60vh)] overflow-y-auto pe-1">
            {items.length === 0 ? (
              <TypographyP2 className="px-3 py-8 text-center text-text-secondary">
                {emptyMessage}
              </TypographyP2>
            ) : isSessions ? (
              sessionGroups.map((group) => (
                <section key={group.key} aria-labelledby={`session-group-${group.key}`}>
                  <div className="sticky top-0 z-10 bg-surface-elevated py-1">
                    <div className="flex h-9 items-center rounded-md bg-surface-container px-3">
                      <TypographyP2
                        id={`session-group-${group.key}`}
                        className="font-medium text-text-primary"
                      >
                        {intl.formatMessage(messages[group.key])}
                      </TypographyP2>
                    </div>
                  </div>
                  {group.items.map((item, index) => (
                    <ContextPickerRowWithSpacing
                      key={linkedContextItemKey(item)}
                      item={item}
                      isSelected={linkedKeys.has(linkedContextItemKey(item))}
                      onPick={onPick}
                      isFirst={index === 0}
                    />
                  ))}
                </section>
              ))
            ) : (
              items.map((item, index) => (
                <ContextPickerRowWithSpacing
                  key={linkedContextItemKey(item)}
                  item={item}
                  isSelected={linkedKeys.has(linkedContextItemKey(item))}
                  onPick={onPick}
                  isFirst={index === 0}
                />
              ))
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

/**
 * Spaces a row from its neighbours with `py-1` (4px each side, 8px between
 * two adjacent rows' fills) instead of a border: the row itself stays
 * border-free and `rounded-lg`, so its hover/selected fill is never cut by a
 * straight edge. The first row of a list/group drops its own top padding
 * (`isFirst`) — there is nothing above it inside its own wrapper to space
 * away from, so keeping that padding would add an extra 4px under the sticky
 * group header or the search field that was never there before.
 */
const ContextPickerRowWithSpacing = ({
  item,
  isSelected,
  onPick,
  isFirst,
}: {
  item: LinkedContextItem;
  isSelected: boolean;
  onPick: (item: LinkedContextItem) => void;
  isFirst: boolean;
}): JSX.Element => (
  <div className={cn('py-1', isFirst && 'pt-0')}>
    <ContextPickerRow item={item} isSelected={isSelected} onPick={onPick} />
  </div>
);

const ContextPickerRow = ({
  item,
  isSelected,
  onPick,
}: {
  item: LinkedContextItem;
  isSelected: boolean;
  onPick: (item: LinkedContextItem) => void;
}): JSX.Element => {
  const intl = useIntl();
  const displayName =
    item.type === 'session'
      ? item.patientName?.trim() || item.sessionName
      : item.type === 'patient'
        ? item.patientName
        : item.type === 'template' || item.type === 'task' || item.type === 'collection'
          ? item.label
          : item.filename;
  const secondaryLabel =
    item.type === 'session' && item.patientName?.trim() ? item.sessionName : undefined;
  const createdAt =
    item.type === 'session' && Number.isFinite(item.createdAt)
      ? item.createdAt
      : undefined;

  return (
    <button
      type="button"
      onClick={() => onPick(item)}
      aria-label={
        secondaryLabel
          ? intl.formatMessage(messages.pickItemAria, {
              name: displayName,
              secondary: secondaryLabel,
            })
          : displayName
      }
      aria-pressed={isSelected}
      className={cn(
        'flex min-h-14 w-full items-center gap-3 px-3 py-2.5 text-start',
        'rounded-lg transition-colors hover:bg-surface-hover',
        focusRing,
        isSelected && 'bg-surface-hover'
      )}
    >
      <Avatar size="sm" color={getAvatarColor(displayName)}>
        <AvatarFallback>{getInitials(displayName).charAt(0)}</AvatarFallback>
      </Avatar>
      <span className="flex min-w-0 flex-1 flex-col">
        <TypographyP2 className="truncate text-text-primary">{displayName}</TypographyP2>
        {secondaryLabel ? (
          <TypographyCaption className="truncate text-text-tertiary">
            {secondaryLabel}
          </TypographyCaption>
        ) : null}
      </span>
      {createdAt !== undefined ? (
        <span className="flex shrink-0 items-center gap-2">
          <span className="flex flex-col items-end">
            <TypographyP2 className="text-text-primary">
              {intl.formatTime(createdAt, {
                hour: 'numeric',
                minute: '2-digit',
              })}
            </TypographyP2>
            <TypographyCaption className="text-text-tertiary">
              {intl.formatDate(createdAt, {
                day: 'numeric',
                month: 'short',
              })}
            </TypographyCaption>
          </span>
          {isSelected ? (
            <Check className="size-4 shrink-0 text-text-primary" aria-hidden />
          ) : null}
        </span>
      ) : isSelected ? (
        <Check className="size-4 shrink-0 text-text-primary" aria-hidden />
      ) : null}
    </button>
  );
};
